import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { ageGatingSuccess, ageGatingFail } from 'Redux/ducks/age-gating';
import AgeGating from 'Components/Utility/Age/AgeGating';
import { ageRatingBoard } from 'Helpers/Geo/data';
import isBot from 'Helpers/SEO/is-bot';

const displayThresholds = {
  ESRB: 17,
  USK: 16,
  PEGI: 18,
  ACB: 18,
};

const AgeGatingContainer = ({
  ratings,
  children,
  passed,
  saveSuccess,
  saveFailure,
  className,
  lazyLoad,
  locked,
  modal,
  closeModal,
  hitcard,
  gateDob,
  accountDob,
  authenticated,
}) => {
  const [failed, setFailed] = useState(false);
  const [dobPass, setDobPass] = useState(false);
  const [failedPreviously, setFailedPreviously] = useState(false); // Has the user tried the age gate in the past and failed?

  const submit = (year, month, day) => {
    if (!year || !month || !day) {
      return setFailed(true);
    }

    const today = Date.now();
    const userDate = Date.UTC(year, month, day);

    const ageDifference = (today - userDate) / 1000 / 60 / 60 / 24 / 363.5;
    if (ageDifference >= (ratings[ageRatingBoard] || 0)) {
      setFailed(false);
      if (modal) { closeModal(); }
      return saveSuccess(userDate);
    }

    setFailed(true);
    return saveFailure(userDate);
  };

  useEffect(() => {
    // If the user has previously failed, we dont want them to be able to try again easily.
    // They must go into their account to confirm their age.
    if (gateDob) {
      const today = Date.now();
      const ageDifference = (today - gateDob) / 1000 / 60 / 60 / 24 / 363.5;
      if (ageDifference < (ratings[ageRatingBoard])) {
        setFailedPreviously(true);
      }
    }

    // Check the users account age. If they are old enough, bypass age gate.
    if (accountDob && accountDob !== '') {
      const dobDate = new Date(accountDob);
      const year = dobDate.getFullYear();
      const month = dobDate.getMonth() + 1;
      const day = dobDate.getDate();
      if (!year || !month || !day) {
        return;
      }
      const today = Date.now();
      const userDate = new Date(year, month, day);
      const ageDifference = (today - userDate) / 1000 / 60 / 60 / 24 / 363.5;
      if (ageDifference >= (ratings[ageRatingBoard])) {
        setDobPass(true);
        return;
      }
      setDobPass(false);
    }
  }, [accountDob, ratings, gateDob]);

  // If any ratings meet the threshold for that ageRatingBoard
  const required = ratings[ageRatingBoard] >= displayThresholds[ageRatingBoard] && !dobPass;

  if (isBot()) {
    return children;
  }

  if ((passed || !required) && !locked) {
    return children;
  }

  return (
    <div className={className}>
      <AgeGating
        failed={failed}
        failedPreviously={failedPreviously}
        hitcard={hitcard}
        lazyLoad={lazyLoad}
        locked={locked}
        passed={passed}
        ratings={ratings}
        submit={submit}
        authenticated={authenticated}
      />
    </div>
  );
};

AgeGatingContainer.propTypes = {
  saveSuccess: PropTypes.func.isRequired,
  saveFailure: PropTypes.func.isRequired,
  passed: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  ratings: PropTypes.shape({
    ACB: PropTypes.number,
    ESRB: PropTypes.number,
    PEGI: PropTypes.number,
    USK: PropTypes.number,
  }),
  lazyLoad: PropTypes.bool,
  locked: PropTypes.bool,
  modal: PropTypes.bool,
  closeModal: PropTypes.func,
  hitcard: PropTypes.bool,
  gateDob: PropTypes.number,
  accountDob: PropTypes.string,
  authenticated: PropTypes.bool,
};

AgeGatingContainer.defaultProps = {
  passed: false,
  className: '',
  children: <React.Fragment />,
  ratings: {},
  lazyLoad: true,
  locked: false,
  modal: false,
  closeModal: () => { },
  hitcard: false,
  gateDob: null,
  accountDob: '',
  authenticated: false,
};

const mapStateToProps = ({
  ageGating: { success, dob: gateDob },
  auth: { dob: accountDob, authenticated },
}) => ({
  passed: success,
  gateDob,
  accountDob,
  authenticated,
});

const mapDispatchToProps = { saveSuccess: ageGatingSuccess, saveFailure: ageGatingFail };

export default connect(mapStateToProps, mapDispatchToProps)(AgeGatingContainer);
